import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, MessageCircle, Clock, Hammer, Star } from 'lucide-react';

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const reasons = [
    {
      icon: Award,
      title: 'Experienced Professionals',
      description:
        'Our team brings years of expertise in home renovation, ensuring every project is executed with precision and skill.',
    },
    {
      icon: MessageCircle,
      title: 'Clear Communication',
      description:
        'We keep you informed throughout every step of your project, with transparent timelines and regular updates.',
    },
    {
      icon: Clock,
      title: 'Reliable & Punctual',
      description:
        'We respect your time and schedule. Our team arrives on time and works efficiently to meet deadlines.',
    },
    {
      icon: Hammer,
      title: 'Quality Craftsmanship',
      description:
        'We use only premium materials and proven techniques to deliver renovations that stand the test of time.',
    },
    {
      icon: Star,
      title: 'Trusted Locally',
      description:
        'Homeowners in Gaithersburg and surrounding areas trust us for their most important renovation projects.',
    },
  ];

  return (
    <section
      id="why-choose-us"
      data-testid="why-choose-us-section"
      ref={ref}
      className="section-padding bg-white"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              Why Choose Us
            </p>
            <h2
              data-testid="why-choose-us-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6 leading-tight"
            >
              The Lux Renovations
              <br />
              Difference
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mb-8" />
            <p className="text-[#71717A] text-base md:text-lg leading-relaxed">
              Choosing the right renovation partner is crucial for your home
              improvement project. At Lux Renovations, we combine expertise,
              integrity, and attention to detail to deliver exceptional results
              every time.
            </p>
          </motion.div>

          {/* Right Column - Features */}
          <div className="space-y-6">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`reason-${index}`}
                className="flex gap-5 p-6 bg-[#F4F4F5] hover:bg-[#0A0A0A] group transition-all duration-300 cursor-default"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-white group-hover:bg-[#D4AF37] flex items-center justify-center transition-colors">
                    <reason.icon className="w-6 h-6 text-[#D4AF37] group-hover:text-[#0A0A0A] transition-colors" />
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-[#0A0A0A] group-hover:text-white mb-2 transition-colors">
                    {reason.title}
                  </h3>
                  <p className="text-sm text-[#71717A] group-hover:text-white/70 leading-relaxed transition-colors">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
